/**
 * Manager for light creation and lifecycle
 */

import {
  DirectionalLight,
  HemisphericLight,
  type Light,
  PointLight,
  type Scene,
  SpotLight,
  Vector3,
} from "@babylonjs/core";
import { BaseManager } from "../core/BaseManager";
import type { LightConfig } from "./types";

/**
 * Manages light creation and lifecycle
 */
export class LightManager extends BaseManager {
  private scene: Scene | null = null;
  private lights = new Map<string, Light>();

  /**
   * Implementation-specific initialization
   */
  protected async onInitialize(): Promise<void> {
    this.emit("lightManagerReady", {});
  }

  /**
   * Set the scene for light management
   * @param scene The scene to manage lights in
   */
  setScene(scene: Scene): void {
    this.ensureInitialized();
    this.scene = scene;
  }

  /**
   * Create a light based on configuration
   * @param config Light configuration
   */
  createLight(config: LightConfig): Light {
    this.ensureInitialized();

    if (!this.scene) {
      throw new Error("Scene not set. Call setScene first.");
    }

    if (this.lights.has(config.name)) {
      throw new Error(`Light with name ${config.name} already exists`);
    }

    let light: Light;

    switch (config.type) {
      case "hemispheric": {
        const direction = config.direction
          ? new Vector3(
              config.direction.x,
              config.direction.y,
              config.direction.z,
            )
          : new Vector3(0, 1, 0);
        light = new HemisphericLight(config.name, direction, this.scene);
        break;
      }
      case "directional": {
        const direction = config.direction
          ? new Vector3(
              config.direction.x,
              config.direction.y,
              config.direction.z,
            )
          : new Vector3(0, -1, 0);
        light = new DirectionalLight(config.name, direction, this.scene);
        break;
      }
      case "point": {
        const position = config.position
          ? new Vector3(config.position.x, config.position.y, config.position.z)
          : new Vector3(0, 10, 0);
        light = new PointLight(config.name, position, this.scene);
        break;
      }
      case "spot": {
        const position = config.position
          ? new Vector3(config.position.x, config.position.y, config.position.z)
          : new Vector3(0, 10, 0);
        const direction = config.direction
          ? new Vector3(
              config.direction.x,
              config.direction.y,
              config.direction.z,
            )
          : new Vector3(0, -1, 0);
        const angle = config.angle ?? Math.PI / 4;
        const exponent = config.exponent ?? 2;
        light = new SpotLight(
          config.name,
          position,
          direction,
          angle,
          exponent,
          this.scene,
        );
        break;
      }
      default:
        throw new Error(
          `Unsupported light type: ${(config as LightConfig).type}`,
        );
    }

    // Apply common properties
    if (config.intensity !== undefined) {
      light.intensity = config.intensity;
    }

    // Store light
    this.lights.set(config.name, light);

    // Emit event
    this.emit("lightCreated", {
      light,
      name: config.name,
      type: config.type,
    });

    return light;
  }

  /**
   * Get a light by name
   * @param name Light name
   */
  getLight(name: string): Light | undefined {
    return this.lights.get(name);
  }

  /**
   * Get all lights
   */
  getAllLights(): Map<string, Light> {
    return new Map(this.lights);
  }

  /**
   * Remove a light
   * @param name Light name
   */
  removeLight(name: string): void {
    const light = this.lights.get(name);
    if (!light) {
      return;
    }

    light.dispose();
    this.lights.delete(name);

    this.emit("lightRemoved", { name });
  }

  /**
   * Clear all lights
   */
  clearAll(): void {
    this.lights.forEach((light) => {
      light.dispose();
    });
    this.lights.clear();
  }

  /**
   * Implementation-specific disposal
   */
  protected onDispose(): void {
    this.clearAll();
    this.scene = null;
  }
}
